// Libs
import classNames from 'classnames/bind';
import { ViewsProps } from 'react-big-calendar';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseTab, BaseTabList } from '~/components/common';
// Others
import { CalendarViewMode, ScheduleMode } from '~/utils/type/common';
import { SCHEDULE_MODE_FULL, SCHEDULE_MODE_MY } from '~/utils/constants/common';
// Styles, images, icons
import styles from './HeaderCalendar.module.scss';
import { icons } from '~/assets';

type Props = {
  views: ViewsProps;
  timeValue: string;
  scheduleMode?: ScheduleMode;
  calendarViewMode: CalendarViewMode;
  onPreviousTime: () => void;
  onNextTime: () => void;
  onToday: () => void;
  onChangeScheduleMode?: (mode: ScheduleMode) => void;
  onChangeCalendarView?: (viewMode: CalendarViewMode) => void;
};

const cx = classNames.bind(styles);

const HeaderCalendar = (props: Props) => {
  //#region Destructuring Props
  const {
    views,
    timeValue,
    scheduleMode = 'full',
    calendarViewMode,
    onPreviousTime,
    onNextTime,
    onToday,
    onChangeScheduleMode,
    onChangeCalendarView,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleChangeScheduleMode = (_event: React.ChangeEvent<{}>, mode: ScheduleMode) => {
    onChangeScheduleMode && onChangeScheduleMode(mode);
  };

  const handleChangeCalendarView = (_event: React.ChangeEvent<{}>, viewMode: CalendarViewMode): void => {
    onChangeCalendarView && onChangeCalendarView(viewMode);
  };
  //#endregion Handle Function

  return (
    <Box className={cx('container')}>
      <Box className={cx('containerTime')}>
        <Box className={cx('actionTime')}>
          <Box className={cx('groupButton')}>
            <IconButton color='primary' className={cx('buttonPrevious')} onClick={onPreviousTime}>
              <img src={icons.scheduleIconPrevious} alt={t('common_alt_img', { ns: 'common/base' })} />
            </IconButton>

            <IconButton color='primary' className={cx('buttonNext')} onClick={onNextTime}>
              <img src={icons.scheduleIconNext} alt={t('common_alt_img', { ns: 'common/base' })} />
            </IconButton>
          </Box>

          <Button variant='outlined' className={cx('buttonToday')} onClick={onToday}>
            {t('schedule_button_today')}
          </Button>
        </Box>

        <Box className={cx('viewTimeValue')}>
          <Typography className={cx('timeValue')}>{timeValue}</Typography>
        </Box>
      </Box>

      {(onChangeScheduleMode || onChangeCalendarView) && (
        <Box className={cx('viewMode')}>
          {onChangeScheduleMode && (
            <Box className={cx('viewScheduleMode')}>
              <BaseTabList
                group={true}
                bordered={false}
                value={scheduleMode}
                onChange={handleChangeScheduleMode}
                centered
                TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
              >
                <BaseTab
                  key={SCHEDULE_MODE_FULL}
                  value={SCHEDULE_MODE_FULL}
                  icon={
                    scheduleMode === SCHEDULE_MODE_FULL ? (
                      <img
                        src={icons.scheduleIconCalendarActive}
                        alt={t('common_alt_img', { ns: 'common/base' })}
                        className={cx('icon')}
                      />
                    ) : (
                      <img
                        src={icons.scheduleIconCalendarInactive}
                        alt={t('common_alt_img', { ns: 'common/base' })}
                        className={cx('icon')}
                      />
                    )
                  }
                  iconPosition='start'
                  label={t('schedule_button_full_schedule')}
                />

                <BaseTab
                  key={SCHEDULE_MODE_MY}
                  value={SCHEDULE_MODE_MY}
                  icon={
                    scheduleMode === 'my' ? (
                      <img
                        src={icons.scheduleIconCalendarActive}
                        alt={t('common_alt_img', { ns: 'common/base' })}
                        className={cx('icon')}
                      />
                    ) : (
                      <img
                        src={icons.scheduleIconCalendarInactive}
                        alt={t('common_alt_img', { ns: 'common/base' })}
                        className={cx('icon')}
                      />
                    )
                  }
                  iconPosition='start'
                  label={t('schedule_button_my_schedule')}
                />
              </BaseTabList>
            </Box>
          )}

          {onChangeCalendarView && (
            <Box className={cx('viewScheduleMode')}>
              <BaseTabList
                group={true}
                bordered={false}
                value={calendarViewMode}
                onChange={handleChangeCalendarView}
                centered
                TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
              >
                {Object.keys(views).map((view: string, index: number) => {
                  return <BaseTab value={view} key={index} label={t(`schedule_button_${view}`)} />;
                })}
              </BaseTabList>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HeaderCalendar;
